import type { ElementNode, ProjectComponent, ProjectContent } from '../store/editor.store'

/**
 * Secciones reutilizables: una sola verdad, muchas instancias.
 *
 * ── Cómo se sincroniza ──────────────────────────────────────────────────────
 * No se sincroniza: no hace falta. Una instancia no guarda contenido, sólo un
 * `componentId`. Sus hijos se resuelven al dibujar, desde el original. Cambiar
 * el original cambia las cinco páginas a la vez porque **nunca hubo cinco
 * copias** — sólo cinco referencias a la misma.
 *
 * La alternativa era guardar una copia en cada página y empujar los cambios al
 * editar. Eso obliga a interceptar toda edición, a decidir qué hacer cuando dos
 * copias divergen, y deja el sitio en un estado a medias si algo falla por el
 * camino. Aquí no hay estado a medias posible.
 *
 * ── Los identificadores se comparten ────────────────────────────────────────
 * Las instancias resueltas traen los ids del original, así que dos en la misma
 * página repiten ids en el DOM. Es la misma decisión que ya tomaron los
 * repetidores del CMS y las partes de un formulario, y trae la misma
 * consecuencia, que está documentada allí: un cambio dentro de un componente
 * reconstruye el lienzo en vez de parchearlo. A cambio, editar cualquier
 * instancia edita el original sin ningún cableado extra.
 */

export function projectComponents(content: ProjectContent | undefined): ProjectComponent[] {
  return content?.components ?? []
}

export function findComponent(content: ProjectContent | undefined, id: string | undefined): ProjectComponent | undefined {
  if (!id) return undefined
  return projectComponents(content).find((component) => component.id === id)
}

/**
 * El nodo listo para dibujar: las instancias, con el contenido del original.
 *
 * Se conserva el `id` de la INSTANCIA en el nodo de fuera —no el del original—
 * porque es lo que la página tiene en su árbol: seleccionarla, moverla o
 * borrarla son operaciones sobre la página, no sobre el componente. Lo que viene
 * del original son los hijos, que es el contenido.
 *
 * Los estilos del nodo de la instancia ganan a los del original: es lo que
 * permite que la misma sección lleve otro margen en una página sin dejar de
 * estar sincronizada en todo lo demás.
 */
export function resolveComponentInstances(
  nodes: ElementNode[],
  content: ProjectContent | undefined,
  /** Guarda contra un componente que se contiene a sí mismo. */
  seen: readonly string[] = [],
): ElementNode[] {
  const components = projectComponents(content)
  if (components.length === 0) return nodes

  const resolve = (node: ElementNode): ElementNode => {
    if (!node.componentId) {
      const children = node.children.length ? node.children.map(resolve) : node.children
      return children === node.children ? node : { ...node, children }
    }

    /* Un componente que se coloca dentro de sí mismo se dibujaría hasta agotar
       la pila. Se corta y se deja el nodo vacío, que es visible y arreglable;
       colgar el editor no lo es. */
    if (seen.includes(node.componentId)) return { ...node, children: [] }

    const master = components.find((item) => item.id === node.componentId)
    /* Sin original —borrado, o un proyecto importado a medias— la instancia se
       queda con lo que tuviera dentro en vez de desaparecer. Perder contenido
       en silencio es el peor final posible para esto. */
    if (!master) return node

    return {
      ...node,
      children: resolveComponentInstances(master.node.children, content, [...seen, node.componentId]),
    }
  }

  return nodes.map(resolve)
}

/**
 * Desvincular: la instancia se queda con el contenido dentro.
 *
 * Es una copia de verdad a partir de ese momento — deja de seguir al original y
 * el original deja de saber de ella. Quien llama tiene que refrescar los ids de
 * los hijos, o dos secciones desvinculadas del mismo original chocarían.
 */
export function detachInstance(node: ElementNode, content: ProjectContent | undefined): ElementNode {
  const master = findComponent(content, node.componentId)
  const rest = { ...node }
  /* Se borra la propiedad en vez de desestructurarla fuera: con
     `exactOptionalPropertyTypes`, dejarla puesta en `undefined` no es lo mismo
     que no tenerla, y el nodo seguiría contando como instancia. */
  delete rest.componentId
  if (!master) return rest
  return { ...rest, children: master.node.children }
}

/** En cuántos sitios se usa: lo que hay que poder decir antes de borrar uno. */
export function countInstances(pagesElements: ElementNode[][], componentId: string): number {
  let total = 0
  const walk = (nodes: ElementNode[]) => {
    for (const node of nodes) {
      if (node.componentId === componentId) total += 1
      if (node.children.length) walk(node.children)
    }
  }
  for (const elements of pagesElements) walk(elements)
  return total
}

/**
 * En qué componente vive un id, si es que vive en alguno.
 *
 * Un hijo de una sección reutilizable NO está en el árbol de la página: la
 * instancia lo tiene vacío y el contenido sale del original al dibujar. Así que
 * cuando alguien selecciona o edita algo de dentro, el id que llega no se
 * encuentra donde se busca siempre. Esto dice dónde buscarlo de verdad.
 *
 * Es el mismo reparto que ya hacen las partes de formulario y las de reseña: el
 * id se resuelve contra el modelo que lo posee, no contra la página.
 */
export function componentOwning(
  content: ProjectContent | undefined,
  elementId: string,
): ProjectComponent | undefined {
  const contains = (node: ElementNode): boolean => (
    node.id === elementId || node.children.some(contains)
  )
  return projectComponents(content).find((component) => contains(component.node))
}
